import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from '../icons/icons'

/**
 * A drop-in replacement for a native `<select>` whose menu is actually themeable.
 *
 * Why this exists: a browser renders a native `<select>`'s option list as an OS popup, and on
 * Windows the highlighted row is painted with the system accent colour — a hard blue that no
 * stylesheet in this repo can reach. `accent-color` is not a dependable fix across platforms, and
 * the popup is not part of the page, so the dropdown always looked foreign inside a BizFirst
 * theme. This renders its own menu from the same `--color-*` tokens as everything else.
 *
 * Behaviour matches the native control closely enough to swap in without other changes: click or
 * Enter/Space to open, Arrow keys to move, Enter to pick, Escape to close, click-outside to
 * dismiss, and focus returns to the trigger after a selection.
 */
export interface ThemedSelectOption {
  value: string | number
  label: string
  disabled?: boolean
}

export interface ThemedSelectProps {
  value: string | number | null | undefined
  onChange: (value: string) => void
  options: ThemedSelectOption[]
  placeholder?: string
  disabled?: boolean
  /** Applied to the outer container, so callers can keep their existing layout rules. */
  className?: string
  /** Accessible name when there is no visible <label> associated with the control. */
  ariaLabel?: string
  id?: string
}

export function ThemedSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  className = '',
  ariaLabel,
  id,
}: ThemedSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const selected = options.find((o) => String(o.value) === String(value ?? ''))

  useEffect(() => {
    if (!isOpen) return
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [isOpen])

  const pick = useCallback(
    (option: ThemedSelectOption) => {
      if (option.disabled) return
      onChange(String(option.value))
      setIsOpen(false)
      setHighlighted(-1)
      // Return focus to the trigger, as a native select does after a choice.
      setTimeout(() => buttonRef.current?.focus(), 0)
    },
    [onChange]
  )

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlighted((p) => (p < options.length - 1 ? p + 1 : p))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlighted((p) => (p > 0 ? p - 1 : 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        setHighlighted((p) => {
          const option = p >= 0 ? options[p] : undefined
          if (option) pick(option)
          return p
        })
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setIsOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, options, pick])

  useEffect(() => {
    if (highlighted < 0 || !menuRef.current) return
    const el = menuRef.current.children[highlighted] as HTMLElement | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlighted])

  return (
    <div ref={containerRef} className={`bf-select ${className}`.trim()}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        className={`bf-select-button${isOpen ? ' open' : ''}`}
        onClick={() => {
          if (disabled) return
          setIsOpen((p) => !p)
          setHighlighted(-1)
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        <span className={`bf-select-value${selected ? '' : ' placeholder'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDownIcon size={16} className={`bf-select-chevron${isOpen ? ' open' : ''}`} />
      </button>

      {isOpen && (
        <div ref={menuRef} className="bf-select-menu" role="listbox" tabIndex={-1}>
          {options.map((option, index) => {
            const isSelected = String(option.value) === String(value ?? '')
            return (
              <button
                key={`${option.value}`}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={
                  'bf-select-option' +
                  (isSelected ? ' selected' : '') +
                  (index === highlighted ? ' highlighted' : '') +
                  (option.disabled ? ' disabled' : '')
                }
                onClick={() => pick(option)}
                onMouseEnter={() => setHighlighted(index)}
                disabled={option.disabled}
              >
                {option.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ThemedSelect
